import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ListChecks, Mic, NotebookPen, Plus, StickyNote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, errText } from "@/lib/api";
import { MarkdownField } from "@/components/MarkdownField";
import { Markdown } from "@/components/Markdown";
import { AttachmentPanel } from "@/components/AttachmentPanel";
import { HearingLogList } from "@/components/HearingLogList";
import { EventRow } from "@/components/EventRow";
import { TodoRow } from "@/components/TodoPanel";
import EventDialog from "@/components/EventDialog";
import { EntryActionsDialog } from "@/components/EntryActionsDialog";
import { TimeSelect } from "@/components/TimeSelect";
import { Switch } from "@/components/ui/switch";
import { EVENT_TYPES, LOG_KINDS, SESSION_FLAGS, isoDate, longDate, shortDate } from "@/lib/eventMeta";
import { paperStyle } from "@/lib/postItStyles";
import { FeeNoteButton } from "@/components/FeeNoteButton";
import { VoiceLogDialog } from "@/components/VoiceLogDialog";
import { useAuth } from "@/context/AuthContext";

export default function MatterDiary() {
  const { matterId } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [data, setData] = useState(null);
  const [briefItems, setBriefItems] = useState([]);
  const [matterTodos, setMatterTodos] = useState([]);
  const [matterNotes, setMatterNotes] = useState([]);
  const [logOpen, setLogOpen] = useState(false);
  const [logForm, setLogForm] = useState(null);
  const [eventOpen, setEventOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [menuEvent, setMenuEvent] = useState(null);
  const [voiceEvent, setVoiceEvent] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/matters/${matterId}/diary`);
      setData(d);
    } catch (e) {
      toast.error(errText(e));
    }
    try {
      const { data: items } = await api.get("/brief/items", { params: { matter_id: matterId } });
      setBriefItems(items);
    } catch {
      setBriefItems([]);
    }
    try {
      const { data: todoItems } = await api.get("/todos", { params: { matter_id: matterId } });
      setMatterTodos(todoItems);
    } catch {
      setMatterTodos([]);
    }
    try {
      const { data: notes } = await api.get("/postits", { params: { matter_id: matterId } });
      setMatterNotes(notes);
    } catch {
      setMatterNotes([]);
    }
  }, [matterId]);

  useEffect(() => {
    load();
  }, [load]);

  const writingUp = (data?.logs || []).some((l) => l.transcribing);

  useEffect(() => {
    if (!writingUp) return;
    const id = setInterval(load, 6000);
    return () => clearInterval(id);
  }, [writingUp, load]);

  const openLog = (log) => {
    setLogForm(log || { date: isoDate(new Date()), kind: "note", body_html: "", matter_id: matterId });
    setLogOpen(true);
  };

  const saveLog = async () => {
    if (!logForm.body_html?.trim()) return toast.error("Write something in the log");
    try {
      const payload = {
        matter_id: matterId,
        event_id: logForm.event_id || null,
        date: logForm.date,
        kind: logForm.kind,
        body_html: logForm.body_html,
        next_date: logForm.next_date || null,
        next_event_type: logForm.next_event_type || "session_based",
        next_start_time: logForm.next_start_time || null,
        next_session_flag: logForm.next_session_flag || "both",
      };
      if (logForm.log_id) await api.put(`/logs/${logForm.log_id}`, payload);
      else await api.post("/logs", payload);
      toast.success(
        logForm.next_date ? "Log saved — next date added to the diary" : "Hearing log saved",
      );
      setLogOpen(false);
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const deleteLog = async (id) => {
    try {
      await api.delete(`/logs/${id}`);
      toast.success("Log deleted");
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  if (!data) return <div className="h-64 surface animate-pulse" data-testid="diary-loading" />;
  const { matter, upcoming, past, logs, court_name: courtName, next_hearing: nextHearing } = data;

  return (
    <div className="space-y-10" data-testid="matter-diary-page">
      <div>
        <Link
          to="/matters"
          data-testid="back-to-matters"
          className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200"
        >
          <ArrowLeft className="h-4 w-4" /> All matters
        </Link>
        <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="label-caps">{matter.case_number || "No case number"}</p>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mt-2 max-w-3xl">
              {matter.title}
            </h1>
            <p className="mt-3 text-sm text-muted-foreground">
              {[matter.client_name, courtName, matter.stage].filter(Boolean).join(" · ") || "—"}
            </p>
            <div className="mt-4">
              {nextHearing ? (
                <span data-testid="matter-next-hearing" className="chip chip-session">
                  Next hearing · {shortDate(nextHearing.date)}
                  {nextHearing.start_time ? ` · ${nextHearing.start_time}` : ""}
                </span>
              ) : (
                <span data-testid="matter-next-hearing" className="chip chip-oos">
                  No next date fixed
                </span>
              )}
            </div>
          </div>
          {canWrite ? (
            <div className="flex flex-wrap gap-2">
              <FeeNoteButton
                matterId={matterId}
                className="rounded-full h-12 px-5"
                testId="diary-fee-note-btn"
              />
              <Button
                variant="outline"
                data-testid="diary-add-hearing-btn"
                onClick={() => {
                  setEditingEvent({ matter_id: matterId, title: matter.title, court_id: matter.court_id });
                  setEventOpen(true);
                }}
                className="rounded-full h-12 px-5"
              >
                <Plus className="h-5 w-5 mr-2" /> Court date
              </Button>
              <Button
                data-testid="diary-add-log-btn"
                onClick={() => openLog(null)}
                className="rounded-full h-12 px-5"
              >
                <NotebookPen className="h-5 w-5 mr-2" /> Hearing log
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <section className="lg:col-span-5 space-y-6">
          <div className="surface p-6" data-testid="upcoming-hearings">
            <h2 className="text-base md:text-lg font-heading font-semibold mb-4">
              Upcoming dates · {upcoming.length}
            </h2>
            <div className="space-y-3">
              {upcoming.length ? (
                upcoming.map((e, i) => (
                  <div key={e.event_id}>
                    <p className="label-caps mb-1.5">{longDate(e.date)}</p>
                    <EventRow
                      event={e}
                      index={i}
                      testIdPrefix="diary-upcoming"
                      onClick={(ev) => setMenuEvent(ev)}
                      onVoiceLog={canWrite ? setVoiceEvent : undefined}
                      onKit={(ev) => navigate(`/kit?event=${ev.event_id}`)}
                    />
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">No future dates listed.</p>
              )}
            </div>
          </div>

          <div className="surface p-6" data-testid="matter-todos">
            <h2 className="text-base md:text-lg font-heading font-semibold mb-4 inline-flex items-center gap-2">
              <ListChecks className="h-5 w-5" /> To-dos · {matterTodos.filter((t) => !t.done).length} open
            </h2>
            <div className="space-y-2">
              {matterTodos.length ? (
                matterTodos.map((item) => (
                  <TodoRow
                    key={item.todo_id}
                    item={item}
                    canWrite={canWrite}
                    onToggle={async (i) => {
                      await api.post(`/todos/${i.todo_id}/toggle`);
                      load();
                    }}
                    onDelete={async (i) => {
                      await api.delete(`/todos/${i.todo_id}`);
                      load();
                    }}
                  />
                ))
              ) : (
                <p className="text-sm text-muted-foreground">
                  No to-dos linked to this matter yet.
                </p>
              )}
            </div>
          </div>

          <div className="surface p-6" data-testid="matter-postits">
            <h2 className="text-base md:text-lg font-heading font-semibold mb-4 inline-flex items-center gap-2">
              <StickyNote className="h-5 w-5" /> Post-its · {matterNotes.length}
            </h2>
            <div className="space-y-3">
              {matterNotes.length ? (
                matterNotes.map((note) => (
                  <Link
                    key={note.post_it_id}
                    to="/postits"
                    data-testid={`matter-postit-${note.post_it_id}`}
                    className="block rounded-sm px-4 py-3 transition-transform duration-200 hover:-translate-y-0.5"
                    style={paperStyle(note.color, note.texture)}
                  >
                    {note.title ? (
                      <p className="font-heading font-bold leading-snug">{note.title}</p>
                    ) : null}
                    <Markdown
                      text={note.body}
                      className="text-sm leading-relaxed line-clamp-4"
                    />
                    {note.expires_on ? (
                      <p className="mt-2 text-xs font-semibold uppercase tracking-wider">
                        {note.expired ? "Expired" : "Until"} {shortDate(note.expires_on)}
                      </p>
                    ) : null}
                  </Link>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">
                  No post-its pinned to this matter yet.
                </p>
              )}
            </div>
          </div>

          <div className="surface p-6" data-testid="matter-brief-items">
            <h2 className="text-base md:text-lg font-heading font-semibold mb-4 inline-flex items-center gap-2">
              <Mic className="h-5 w-5" /> Associate instructions · {briefItems.length}
            </h2>
            <div className="space-y-3">
              {briefItems.length ? (
                briefItems.map((item) => (
                  <div
                    key={item.item_id}
                    data-testid={`matter-brief-item-${item.item_id}`}
                    className="border border-border rounded-sm px-4 py-3"
                  >
                    <p className="label-caps">{longDate(item.date)}</p>
                    <Markdown
                      text={item.instruction}
                      className="mt-2 text-base leading-relaxed"
                    />
                    <div className="mt-2 flex flex-wrap gap-2">
                      {item.bare_acts ? (
                        <span className="chip chip-session chip-wrap">
                          Bare Acts · {item.bare_acts}
                        </span>
                      ) : null}
                      {item.judgments ? (
                        <span className="chip chip-post chip-wrap">
                          Judgments · {item.judgments}
                        </span>
                      ) : null}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">
                  No dictated instructions for this matter yet.
                </p>
              )}
            </div>
          </div>

          <div className="surface p-6" data-testid="past-hearings">
            <h2 className="text-base md:text-lg font-heading font-semibold mb-4">
              Past dates · {past.length}
            </h2>
            <div className="space-y-3">
              {past.length ? (
                past.map((e, i) => (
                  <div key={e.event_id}>
                    <p className="label-caps mb-1.5">{longDate(e.date)}</p>
                    <EventRow event={e} index={i} testIdPrefix="diary-past" />
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">No history yet.</p>
              )}
            </div>
          </div>
        </section>

        <section className="lg:col-span-7 surface p-6" data-testid="hearing-logs">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <h2 className="text-base md:text-lg font-heading font-semibold">
              Hearing logs · {logs.length}
            </h2>
            {canWrite ? (
              <Button
                variant="outline"
                data-testid="matter-voice-log-btn"
                onClick={() =>
                  setVoiceEvent(
                    nextHearing ||
                      upcoming[0] || {
                        event_id: null,
                        matter_id: matterId,
                        date: isoDate(new Date()),
                        title: matter.title,
                        case_number: matter.case_number,
                      },
                  )
                }
                className="rounded-full h-11 px-5"
              >
                <Mic className="h-4 w-4 mr-2" /> Dictate a hearing note
              </Button>
            ) : null}
          </div>
          <HearingLogList
            logs={logs}
            canWrite={canWrite}
            onEdit={openLog}
            onDelete={deleteLog}
            onChanged={load}
            openLogId={params.get("log")}
            onOpenedLog={() => {
              const next = new URLSearchParams(params);
              next.delete("log");
              setParams(next, { replace: true });
            }}
          />
        </section>
      </div>

      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent
          className="rounded-sm max-w-2xl max-h-[75vh] flex flex-col"
          data-testid="log-dialog"
        >
          <DialogHeader className="shrink-0">
            <DialogTitle className="font-heading text-2xl">
              {logForm?.log_id ? "Edit hearing log" : "New hearing log"}
            </DialogTitle>
          </DialogHeader>
          {logForm ? (
            <div className="space-y-4 py-2 flex-1 overflow-y-auto -mx-1 px-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="label-caps">Date</Label>
                  <Input
                    type="date"
                    data-testid="log-date-input"
                    value={logForm.date}
                    onChange={(e) => e.target.value && setLogForm({ ...logForm, date: e.target.value })}
                    onClick={(e) => {
                      try {
                        e.currentTarget.showPicker?.();
                      } catch {
                        /* browsers that refuse showPicker still open on their own */
                      }
                    }}
                    className="rounded-sm h-11 text-base"
                  />
                  <p className="text-xs text-muted-foreground">{longDate(logForm.date)}</p>
                </div>
                <div className="space-y-2">
                  <Label className="label-caps">Kind</Label>
                  <Select
                    value={logForm.kind}
                    onValueChange={(v) => setLogForm({ ...logForm, kind: v })}
                  >
                    <SelectTrigger data-testid="log-kind-select" className="rounded-sm h-11 text-base">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LOG_KINDS.map((k) => (
                        <SelectItem key={k.value} value={k.value}>
                          {k.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="label-caps">What happened</Label>
                <MarkdownField
                  key={logForm.log_id || "new"}
                  value={logForm.body_html}
                  onChange={(html) => setLogForm({ ...logForm, body_html: html })}
                  mode="html"
                  minHeight={160}
                  maxHeight={260}
                  testId="log-editor"
                  placeholder="What the court said, directions given, the order in short"
                />
              </div>

              <div className="border border-border rounded-sm p-4 space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-base font-semibold">Adjourned to a next date</p>
                    <p className="text-sm text-muted-foreground">
                      Adds the entry straight into your diary for this matter
                    </p>
                  </div>
                  <Switch
                    data-testid="log-next-date-switch"
                    checked={!!logForm.next_date}
                    onCheckedChange={(v) =>
                      setLogForm({
                        ...logForm,
                        next_date: v ? isoDate(new Date(Date.now() + 14 * 86400000)) : null,
                      })
                    }
                  />
                </div>

                {logForm.next_date ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="label-caps">Next date</Label>
                        <Input
                          type="date"
                          data-testid="log-next-date-input"
                          value={logForm.next_date}
                          onChange={(e) =>
                            e.target.value && setLogForm({ ...logForm, next_date: e.target.value })
                          }
                          onClick={(e) => {
                            try {
                              e.currentTarget.showPicker?.();
                            } catch {
                              /* browsers that refuse showPicker still open on their own */
                            }
                          }}
                          className="rounded-sm h-11 text-base"
                        />
                        <p className="text-xs text-muted-foreground">
                          {longDate(logForm.next_date)}
                        </p>
                      </div>
                      {logForm.next_event_type === "session_based" ? (
                        <div className="space-y-2">
                          <Label className="label-caps">Session</Label>
                          <Select
                            value={logForm.next_session_flag || "both"}
                            onValueChange={(v) => setLogForm({ ...logForm, next_session_flag: v })}
                          >
                            <SelectTrigger
                              data-testid="log-next-session-select"
                              className="rounded-sm h-11 text-base"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {SESSION_FLAGS.map((s) => (
                                <SelectItem key={s.value} value={s.value}>
                                  {s.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Label className="label-caps">Time</Label>
                          <TimeSelect
                            value={logForm.next_start_time}
                            onChange={(v) => setLogForm({ ...logForm, next_start_time: v })}
                            testId="log-next-time-select"
                          />
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      {EVENT_TYPES.filter((t) => t.value !== "out_of_station").map((t) => (
                        <button
                          key={t.value}
                          type="button"
                          data-testid={`log-next-type-${t.value}`}
                          onClick={() => setLogForm({ ...logForm, next_event_type: t.value })}
                          className={`px-3 py-3 rounded-sm border text-sm font-semibold transition-colors duration-200 ${
                            (logForm.next_event_type || "session_based") === t.value
                              ? "bg-primary text-primary-foreground border-primary"
                              : "border-border hover:bg-accent"
                          }`}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>

              {logForm.log_id ? (
                <div className="space-y-2">
                  <Label className="label-caps">Attachments</Label>
                  <AttachmentPanel
                    logId={logForm.log_id}
                    attachments={
                      logs.find((l) => l.log_id === logForm.log_id)?.attachments || []
                    }
                    canWrite={canWrite}
                    onChange={load}
                  />
                </div>
              ) : null}
            </div>
          ) : null}
          <DialogFooter className="shrink-0 border-t border-border pt-4">
            <Button variant="outline" className="rounded-full h-11" onClick={() => setLogOpen(false)}>
              Cancel
            </Button>
            <Button data-testid="log-save-btn" onClick={saveLog} className="rounded-full h-11 px-6">
              Save log
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EntryActionsDialog
        event={menuEvent}
        onOpenChange={(v) => !v && setMenuEvent(null)}
        onEdit={(ev) => {
          setMenuEvent(null);
          setEditingEvent(ev);
          setEventOpen(true);
        }}
        onChanged={load}
      />

      <EventDialog
        open={eventOpen}
        onOpenChange={setEventOpen}
        event={editingEvent}
        onSaved={load}
        onVoiceLog={canWrite ? setVoiceEvent : undefined}
      />

      <VoiceLogDialog
        open={Boolean(voiceEvent)}
        onOpenChange={(v) => !v && setVoiceEvent(null)}
        event={voiceEvent}
        onSaved={load}
      />
    </div>
  );
}
